import { Modal, Radio, Table } from 'antd';
import {
  Button,
  CheckboxButton,
  CloseIcon,
  Datepicker,
  Dropdown,
  Feedback,
  Input,
  Paragrapgh,
  RadioButton,
  Subtitle,
} from '../../shared';
import {
  IFarmResource,
  IOysterLineResource,
  ISpatStorageResource,
} from '../../../entities/farms.entities';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  divideEqually,
  pcsToDoz,
  toMillisecond,
  toSecond,
  dozToPcs,
  calcDiffDays,
  defaultDateFormat,
  numToStr,
  getUnitOfSpecie,
} from '../../../lib/common.helpers';
import SignatureCanvas from 'react-signature-canvas';
import FilesWall from '../../shared/pictures-wall/FilesWall';
import { sendSingleRequest } from '../../../apis';
import {
  loadFarmsData,
  loadQueueData,
} from '../../../store/farms/farms.actions';
import { loadInventories } from '../../../store/inventories/inventories.actions';
import { showFeedback } from '../../../store/ui/ui.actions';
import { IFileWall, ITimeRange } from '../../../entities/general.entities';
import { toSecTimeTracks } from '../../shared/input/TimeTrackInput';
import {
  selectProfile,
  selectAccount,
} from '../../../store/auth/auth.selector';
import { translate } from '../../../lib/lang.helper';
import { selectLang } from '../../../store/ui/ui.selector';
import {
  SelectAccountSetting,
  SelectCropSpeciesByFarmID,
  SelectFarmsByFarmID,
  SelectUtilsByFarmID,
} from '../../../store/extra/extra.selector';
import MoreFieldsForm from '../HarvestCompleteModal/MoreFieldsForm';
import HarvestBatchSelect, {
  HarvestGroupLensForm,
} from '../HarvestCompleteModal/HarvestBatchSelect';
import { selectSpatStorages } from '../../../store/farms/farms.selector';
import { ICropSpecie } from '../../../entities/utils.entities';
import { calcOysterPercent, getLineOysters } from '../../../lib/farm.helpers';
import ModalFeedbackView from '../../shared/feedback/ModalFeedbackView';

interface IPickedItem {
  inventory_id: number;
  seeding_id: number;
  quantity: number | null;
}

interface InventoryProps {
  lineData: IOysterLineResource;
  form: IPickedItem[];
  setForm: (form: IPickedItem[]) => void;
}

const ReturningInventories = ({ lineData, form, setForm }: InventoryProps) => {
  const lang = useSelector(selectLang);
  const usedInventories = lineData.growing_cycle?.inventories_sum ?? [];

  const updateForm = (ivt_id: number, val: string) => {
    const ms = lineData.growing_cycle?.main_seed;
    if (!ms) return;

    const v = val.length <= 0 ? null : Number(val);
    let tmp = [...form];
    const i = tmp.findIndex(x => x.inventory_id === ivt_id);
    if (i < 0) {
      tmp.push({ inventory_id: ivt_id, seeding_id: ms.id, quantity: v });
    } else {
      tmp[i].quantity = v;
    }
    setForm(tmp);
  };

  return usedInventories.length > 0 ? (
    <div className='mt-17'>
      <Paragrapgh
        size={2}
        color='black'
        align='left'
        fontWeight={400}
        className='pb-12'
      >
        {translate(
          lang,
          'Please input quantity of inventories that are being harvested',
        )}
      </Paragrapgh>
      <div className='d-flex justify-between align-center'>
        {usedInventories.map(item => (
          <div className='mb-17' key={item.inventory_id}>
            <Input
              type='number'
              onChange={e => updateForm(item.inventory_id, e.target.value)}
              value={numToStr(
                form.find(x => x.inventory_id === item.inventory_id)?.quantity,
              )}
              label={`${item.inventory_name}`}
              placeholder={translate(
                lang,
                'Maximum value is %s',
                item.quantity,
              )}
              max={item.quantity}
            />
          </div>
        ))}
      </div>
    </div>
  ) : (
    <></>
  );
};

interface IHarvestGroup {
  batch_seed_id: number;
  amount?: number;
  line_length?: number;
}

interface IAmount {
  specie_id: number;
  amount?: number;
}

interface ILineData {
  line_id: number;
  is_full: boolean;
  is_final: boolean;
  line_length: number | null;
  inventories?: IPickedItem[];
  harvest_groups?: IHarvestGroup[];
  is_combined?: boolean;
  combined_batch_id?: number | null;

  amount_dz?: number | null;
  amount_pcs?: number | null;
  return_amount_dz?: number | null;
  return_amount_pcs?: number | null;
  mortality?: number | null;
  harvest_species?: IAmount[];
}

interface IFormData {
  complete_date: number;
  amount_dz: number | null;
  amount_pcs: number | null;
  return_amount_dz: number | null;
  return_amount_pcs: number | null;
  mortality: number | null;
  shell_length: number | null;
  shell_length_max: number | null;
  comment?: string;
  image_pdf_files?: IFileWall[];
  signature?: string;
  time_tracks?: ITimeRange[];
  port_of_unload?: string;
  delivered_to?: string;
  growing_area?: string;
  crop_owner?: string;
  packhouse?: string;
  shell_condition?: string;
  harvester_name?: string;
  oyster_type?: string;
  harvest_number?: string;
  measurement_units?: 'pcs' | 'dz';
  harvest_species?: IAmount[];
}

const defaultForm: IFormData = {
  complete_date: Date.now(),
  amount_dz: null,
  amount_pcs: null,
  return_amount_dz: null,
  return_amount_pcs: null,
  mortality: null,
  shell_length: null,
  shell_length_max: null,
  image_pdf_files: [],
  measurement_units: 'dz',
};

const getNewLineData = (
  lines: IOysterLineResource[],
  lineId: number,
  cropSpecies: ICropSpecie[],
  autoFillDisabled: boolean | undefined,
  isMergedMode: boolean,
): ILineData => {
  const line = lines.find(y => y.id === lineId);
  const totDozens = line ? getLineOysters(line) : 0;

  const harvest_species = isMergedMode
    ? undefined
    : cropSpecies.map(x => {
        const percent = calcOysterPercent(x, lineId);
        const amount = Math.round((percent ?? 0) * (totDozens ?? 0)) / 100;
        return {
          specie_id: x.id,
          amount: !autoFillDisabled ? amount : undefined,
        };
      });

  return {
    line_id: lineId,
    is_full: true,
    is_final: true,
    amount_dz: isMergedMode ? undefined : line ? (totDozens ?? null) : null,
    amount_pcs: isMergedMode
      ? undefined
      : line
        ? totDozens !== undefined
          ? dozToPcs(totDozens)
          : null
        : null,
    line_length: line?.growing_cycle
      ? line.growing_cycle.current_basket_count
      : null,
    return_amount_dz: null,
    return_amount_pcs: null,
    harvest_species,
  };
};

interface LineSectionProps {
  line: ILineData;
  isMergedMode: boolean;
  allLines: IOysterLineResource[];
  lineOptions: Array<{
    id: string;
    value: string;
    label: string;
    farming_method?: string | null;
  }>;
  weekBatches: ISpatStorageResource[];
  sizedOysters: ICropSpecie[];
  measureUnit: 'pcs' | 'dz';
  isTrackMortality: boolean | undefined;
  updateHarvestedLine: (lineId: number, key: string, value: any) => void;
  getSpatName: (x: ISpatStorageResource) => string;
  updateOysterSpecies: (
    lineId: number | undefined,
    specieId: number,
    value: string,
  ) => void;
}

const LineSection = ({
  line,
  isMergedMode,
  allLines,
  lineOptions,
  weekBatches,
  sizedOysters,
  measureUnit,
  isTrackMortality,
  updateHarvestedLine,
  getSpatName,
  updateOysterSpecies,
}: LineSectionProps) => {
  const lang = useSelector(selectLang);
  const lineSrc = allLines.find(x => x.id === line.line_id);
  const species = sizedOysters.filter(x => {
    const percent = calcOysterPercent(x, line.line_id);
    return percent !== undefined && percent > 0;
  });

  const metaColumns = (lineID: number, species: ICropSpecie[]) => [
    {
      key: 'label',
      title: 'Name',
      render: (x: IAmount) => {
        const specie = species.find(y => y.id === x.specie_id);
        return (
          <span>
            {specie?.subtype
              ? `${specie.subtype} - ${specie.name}`
              : specie?.name}
          </span>
        );
      },
    },
    {
      key: 'value',
      title: 'Dozens',
      render: (row: IAmount) => (
        <Input
          type='number'
          label=''
          unit={measureUnit}
          value={numToStr(row.amount)}
          onChange={e =>
            updateOysterSpecies(lineID, row.specie_id, e.target.value)
          }
        />
      ),
    },
  ];

  return (
    <div className='section' key={line.line_id}>
      <Subtitle color='black-2' size={0} fontWeight={600} align='left'>
        {translate(
          lang,
          'Detail of %s',
          lineOptions.find(x => Number(x.id) === line.line_id)?.label ?? '',
        )}
      </Subtitle>
      {isMergedMode ? (
        <div className='d-flex'>
          <div className='flex-1 mr-12'>
            <ReturningInventories
              lineData={
                allLines.find(x => x.id === line.line_id) as IOysterLineResource
              }
              form={line.inventories ?? []}
              setForm={v => updateHarvestedLine(line.line_id, 'inventories', v)}
            />
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(
                  lang,
                  'Number of %s',
                  lineOptions.find(x => Number(x.id) === line.line_id)
                    ?.farming_method ?? '',
                )}
                value={numToStr(line.line_length)}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    'line_length',
                    e.target.value,
                  )
                }
              />
            </div>
          </div>
          <div className='flex-1 ml-12'>
            <Dropdown
              label={translate(lang, '_line_harvested_final')}
              value={line.is_final ? 'yes' : 'no'}
              options={[
                {
                  id: 'yes',
                  value: 'yes',
                  label: translate(lang, 'Yes, this is final harvest'),
                },
                {
                  id: 'no',
                  value: 'no',
                  label: translate(lang, '_itm_harvest'),
                },
              ]}
              onChange={v =>
                updateHarvestedLine(line.line_id, 'is_final', v === 'yes')
              }
              className='mb-17'
            />
            <Dropdown
              label={translate(lang, '_harvested_entire_line')}
              value={!!line.is_full ? 'full' : 'partial'}
              options={[
                {
                  id: 'full',
                  value: 'full',
                  label: translate(lang, '_yes_line_now_empty'),
                },
                {
                  id: 'partial',
                  value: 'partial',
                  label: translate(lang, '_crop'),
                },
              ]}
              onChange={v =>
                updateHarvestedLine(line.line_id, 'is_full', v === 'full')
              }
            />
            {line.is_full === false &&
              (lineSrc?.growing_cycle?.seedings.length ?? 0) > 0 && (
                <div className='mb-17 mt-17'>
                  <HarvestBatchSelect
                    type='OYSTER'
                    farmingMethod={lineSrc?.farming_method}
                    growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                    values={
                      line.harvest_groups?.map(x => x.batch_seed_id) ?? []
                    }
                    onChange={v =>
                      updateHarvestedLine(
                        line.line_id,
                        'harvest_groups',
                        v
                          ? v.map(x => ({
                              batch_seed_id: x,
                              line_length: line.harvest_groups?.find(
                                y => y.batch_seed_id === x,
                              )?.line_length,
                            }))
                          : undefined,
                      )
                    }
                  />
                  <HarvestGroupLensForm
                    type='OYSTER'
                    farmingMethod={lineSrc?.farming_method}
                    growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                    values={line.harvest_groups}
                    onChange={vals =>
                      updateHarvestedLine(line.line_id, 'harvest_groups', vals)
                    }
                    className='mt-17'
                  />
                </div>
              )}
            <div className='mt-17'>
              <CheckboxButton
                checked={line.is_combined}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    'is_combined',
                    e.target.checked,
                  )
                }
                label={translate(
                  lang,
                  'Combine graded oysters into daily spat',
                )}
              />
            </div>
            {!!line.is_combined && (
              <div className='mt-17 mb-7'>
                <Dropdown
                  label={translate(lang, '_sel_spat_storage_combine')}
                  options={weekBatches.map(x => ({
                    id: x.id.toString(),
                    value: x.id.toString(),
                    label: getSpatName(x),
                  }))}
                  value={numToStr(line.combined_batch_id)}
                  onChange={v =>
                    updateHarvestedLine(
                      line.line_id,
                      'combined_batch_id',
                      v ? Number(v) : undefined,
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className='d-flex mb-24'>
          <div className='flex-1 mr-12'>
            <ReturningInventories
              lineData={
                allLines.find(x => x.id === line.line_id) as IOysterLineResource
              }
              form={line.inventories ?? []}
              setForm={v => updateHarvestedLine(line.line_id, 'inventories', v)}
            />
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(
                  lang,
                  'Number of %s',
                  lineOptions.find(x => Number(x.id) === line.line_id)
                    ?.farming_method ?? '',
                )}
                value={numToStr(line.line_length)}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    'line_length',
                    e.target.value,
                  )
                }
              />
            </div>
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(lang, 'Amount')}
                value={numToStr(
                  measureUnit === 'pcs' ? line.amount_pcs : line.amount_dz,
                )}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    measureUnit === 'pcs' ? 'amount_pcs' : 'amount_dz',
                    e.target.value,
                  )
                }
                unit={measureUnit}
              />
            </div>
            <div className='mb-17'>
              <Input
                type='number'
                label={translate(lang, '_graded_oysters')}
                value={numToStr(
                  measureUnit === 'pcs'
                    ? line.return_amount_pcs
                    : line.return_amount_dz,
                )}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    measureUnit === 'pcs'
                      ? 'return_amount_pcs'
                      : 'return_amount_dz',
                    e.target.value,
                  )
                }
                unit={measureUnit}
              />
            </div>
            <Dropdown
              label='F / P'
              value={!!line.is_full ? 'full' : 'partial'}
              options={[
                {
                  id: 'full',
                  value: 'full',
                  label: translate(lang, 'Full'),
                },
                {
                  id: 'partial',
                  value: 'partial',
                  label: translate(lang, 'Partial'),
                },
              ]}
              onChange={v =>
                updateHarvestedLine(line.line_id, 'is_full', v === 'full')
              }
            />
            {line.is_full === false &&
              (lineSrc?.growing_cycle?.seedings.length ?? 0) > 0 && (
                <div className='mb-17 mt-17'>
                  <HarvestBatchSelect
                    type='OYSTER'
                    farmingMethod={lineSrc?.farming_method}
                    growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                    values={
                      line.harvest_groups?.map(x => x.batch_seed_id) ?? []
                    }
                    onChange={v =>
                      updateHarvestedLine(
                        line.line_id,
                        'harvest_groups',
                        v ? v.map(x => ({ batch_seed_id: x })) : undefined,
                      )
                    }
                  />
                  <HarvestGroupLensForm
                    type='OYSTER'
                    farmingMethod={lineSrc?.farming_method}
                    growingSeeds={lineSrc?.growing_cycle?.seedings ?? []}
                    values={line.harvest_groups}
                    onChange={vals =>
                      updateHarvestedLine(line.line_id, 'harvest_groups', vals)
                    }
                    className='mt-17'
                  />
                </div>
              )}
          </div>
          <div className='flex-1 ml-12'>
            <Table
              rowKey={'specie_id'}
              className='table table-bordered table--units table--small'
              dataSource={line.harvest_species?.filter(y =>
                species.some(x => x.id === y.specie_id),
              )}
              columns={metaColumns(line.line_id, species)}
              pagination={false}
            />
            {!!isTrackMortality && (
              <div className='mt-16'>
                <Input
                  label={translate(lang, 'Mortality')}
                  type='number'
                  onChange={e =>
                    updateHarvestedLine(
                      line.line_id,
                      'mortality',
                      e.target.value,
                    )
                  }
                  value={numToStr(line.mortality)}
                  unit={measureUnit}
                />
              </div>
            )}
            <div className='mt-17'>
              <CheckboxButton
                checked={line.is_combined}
                onChange={e =>
                  updateHarvestedLine(
                    line.line_id,
                    'is_combined',
                    e.target.checked,
                  )
                }
                label={translate(
                  lang,
                  'Combine graded oysters into daily spat',
                )}
              />
            </div>
            {!!line.is_combined && (
              <div className='mt-17 mb-7'>
                <Dropdown
                  label={translate(lang, '_sel_spat_storage_combine')}
                  options={weekBatches.map(x => ({
                    id: x.id.toString(),
                    value: x.id.toString(),
                    label: getSpatName(x),
                  }))}
                  value={numToStr(line.combined_batch_id)}
                  onChange={v =>
                    updateHarvestedLine(
                      line.line_id,
                      'combined_batch_id',
                      v ? Number(v) : undefined,
                    )
                  }
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

interface Props {
  visible: boolean;
  title?: string;
  data?: any;
  farm?: IFarmResource;
  onCancel: () => void;
  onConfirm: (data: any) => void;
  overConfirm?: (data: any) => void;
  onlyView?: boolean;
  confirmID?: number;
  warningMessage?: string;
}

const OysterBulkHarvestModal = ({
  visible,
  title,
  farm: farmData,
  data: paramData,
  onCancel,
  onConfirm,
  overConfirm,
  onlyView,
  confirmID,
  warningMessage,
}: Props) => {
  const dispatch = useDispatch<any>();

  const lang = useSelector(selectLang);
  const unitType = useSelector(selectAccount)?.display_unit;
  const farmsData = SelectFarmsByFarmID(farmData?.id);
  const farmUtils = SelectUtilsByFarmID(farmData?.id);
  const profile = useSelector(selectProfile);
  const port_of_unloads = farmUtils.filter(x => x.type === 'port_of_unload'),
    delivered_tos = farmUtils.filter(x => x.type === 'delivered_to'),
    packhouses = farmUtils.filter(x => x.type === 'packhouse');
  const sizedOysters = SelectCropSpeciesByFarmID(farmData?.id);
  const weekBatches = useSelector(selectSpatStorages)
    .filter(x => calcDiffDays(x.collection_date) <= 7)
    .filter(x => x.type === 'OYSTER' && !!x.harvest)
    .sort((a, b) => b.collection_date - a.collection_date);
  const {
    hide_harvest_species: hideHarvestSpecies,
    auto_fill_harvest_species_disabled: autoFillDisabled,
  } = SelectAccountSetting(farmData?.id) ?? {};

  const allLines = useMemo(() => {
    let result: IOysterLineResource[] = [];
    for (let farm of farmsData) {
      for (let i = 0; i < farm.lines.length; i++) {
        const line = farm.lines[i];
        if (line.crop_type === 'OYSTER' && line.growing_cycle) {
          result.push(line as IOysterLineResource);
        }
      }
    }
    return result;
  }, [farmsData]);

  const linesOptions = allLines
    .filter(x => !!x.growing_cycle && x.crop_type === 'OYSTER')
    .map(x => ({
      id: x.id.toString(),
      value: x.id.toString(),
      label: `${x.farm_name} (${x.farm_number}) - ${x.line_name}`,
      farming_method: x.farming_method,
    }));

  const signatureCanvas = useRef<SignatureCanvas | null>();
  const [isMergedMode, setIsMergedMode] = useState<boolean>(
    paramData && paramData.lines
      ? paramData.lines.every((ln: any) => !ln.amount_pcs)
      : true,
  );
  const [formData, setFormData] = useState<IFormData>(
    paramData
      ? {
          ...defaultForm,
          ...paramData,
          complete_date: paramData.complete_date
            ? toMillisecond(paramData.complete_date)
            : Date.now(),
          time_tracks: paramData.time_tracks
            ? paramData.time_tracks.map((x: any) => ({
                start_time: toMillisecond(x.start_time),
                finish_time: toMillisecond(x.finish_time),
              }))
            : [],
          mortality: paramData.mortality
            ? paramData.measurement_units === 'pcs'
              ? paramData.mortality
              : pcsToDoz(paramData.mortality)
            : paramData.mortality,
        }
      : {
          ...defaultForm,
          port_of_unload:
            port_of_unloads && port_of_unloads.length > 0
              ? port_of_unloads[0].name
              : defaultForm.port_of_unload,
          delivered_to:
            delivered_tos && delivered_tos.length > 0
              ? delivered_tos[0].name
              : defaultForm.delivered_to,
          packhouse:
            packhouses && packhouses.length > 0
              ? packhouses[0].name
              : defaultForm.packhouse,
          crop_owner:
            farmData && farmData.owners && farmData.owners.length > 0
              ? farmData.owners[0].title
              : null,
          growing_area: farmData?.name,
          harvester_name: profile?.name ?? null,
        },
  );
  const [harvestedLines, setHarvestedLines] = useState<ILineData[]>(
    paramData && paramData.lines
      ? paramData.lines.map((ln: ILineData) => ({
          ...ln,
          mortality: ln.mortality
            ? formData.measurement_units === 'pcs'
              ? ln.mortality
              : pcsToDoz(ln.mortality)
            : ln.mortality,
        }))
      : [],
  );
  const [disabled, setDisabled] = useState(false);
  const [sigCanLoaded, setSigCanLoaded] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>();

  const getSpatName = (x: ISpatStorageResource) => {
    let line = x.source;
    line +=
      x.harvest && x.harvest.complete_date !== null
        ? ' [' + defaultDateFormat(x.harvest.complete_date) + ']'
        : '';
    return line;
  };
  const updateOysterSpecies = (
    lineId: number | undefined,
    specieId: number,
    value: string,
  ) => {
    if (!lineId) {
      const amount = value.length > 0 ? Number(value) : undefined;
      let tmpSpecies = formData.harvest_species
        ? [...formData.harvest_species]
        : [];
      const i = tmpSpecies.findIndex(x => x.specie_id === specieId);
      if (i < 0) {
        tmpSpecies.push({
          specie_id: specieId,
          amount,
        });
      } else {
        tmpSpecies[i].amount = amount;
      }
      const sum = tmpSpecies.reduce((p, c) => p + (c.amount ?? 0), 0);
      setFormData({
        ...formData,
        harvest_species: tmpSpecies,
        amount_dz: formData.measurement_units === 'pcs' ? pcsToDoz(sum) : sum,
        amount_pcs: formData.measurement_units === 'pcs' ? sum : dozToPcs(sum),
      });
    } else {
      let tmp = [...harvestedLines];
      const i = harvestedLines.findIndex(x => x.line_id === lineId);
      if (i > -1) {
        tmp[i].harvest_species = tmp[i].harvest_species?.map(x => {
          if (x.specie_id === specieId) {
            return {
              ...x,
              amount: value.length > 0 ? Number(value) : undefined,
            };
          } else {
            return x;
          }
        });
        const sum =
          tmp[i].harvest_species?.reduce((p, c) => p + (c.amount ?? 0), 0) ?? 0;
        tmp[i].amount_dz =
          formData.measurement_units === 'pcs' ? pcsToDoz(sum) : sum;
        tmp[i].amount_pcs =
          formData.measurement_units === 'pcs' ? sum : dozToPcs(sum);
      }
      setHarvestedLines(tmp);
    }
  };
  const updateHarvestedLine = (lineId: number, key: string, value: any) => {
    if (
      !['is_full', 'inventories', 'harvest_groups', 'harvest_species'].includes(
        key,
      )
    ) {
      value = value.length <= 0 ? null : Number(value);
    }
    let tmp = [...harvestedLines];
    const i = harvestedLines.findIndex(x => x.line_id === lineId);
    if (i > -1) {
      tmp[i] = { ...tmp[i], [key]: value };
      if (key === 'is_full') {
        const dt = getNewLineData(
          allLines,
          lineId,
          sizedOysters,
          autoFillDisabled,
          isMergedMode,
        );
        if (!isMergedMode) {
          tmp[i].amount_dz = value ? dt.amount_dz : tmp[i].amount_dz;
          tmp[i].amount_pcs = value ? dt.amount_pcs : tmp[i].amount_pcs;
        } else {
          tmp[i].amount_dz = undefined;
          tmp[i].amount_pcs = undefined;
        }
        tmp[i].line_length = value ? dt.line_length : tmp[i].line_length;
      } else if (key === 'is_combined') {
        const combined_batch_id =
          weekBatches.length > 0 && value ? weekBatches[0].id : undefined;
        tmp[i].combined_batch_id = combined_batch_id;
      } else if (key === 'inventories') {
        const basketSum = tmp[i].inventories?.reduce(
          (p, c) => p + (c.quantity ?? 0),
          0,
        );
        tmp[i].line_length = basketSum ?? tmp[i].line_length;
      } else if (key === 'line_length') {
        if (isMergedMode) {
          const amountDozSum = allLines.reduce((p, line) => {
            let density = line.growing_cycle?.main_seed.density ?? 0;
            if (line.growing_cycle?.main_seed.amount_unit === 'pcs') {
              density = pcsToDoz(density);
            }
            const length =
              tmp.find(x => x.line_id === line.id)?.line_length ?? 0;
            const doz = length * density;
            return p + doz;
          }, 0);
          setFormData(prv => ({
            ...prv,
            amount_dz: amountDozSum,
            amount_pcs: dozToPcs(amountDozSum),
          }));
        } else {
          const line = allLines.find(x => x.id === lineId);
          let density = line?.growing_cycle?.main_seed.density ?? 0;
          if (line?.growing_cycle?.main_seed.amount_unit === 'pcs') {
            density = pcsToDoz(density);
          }
          tmp[i].amount_dz = Math.round(Number(value) * density);
          tmp[i].amount_pcs = dozToPcs(Number(tmp[i].amount_dz));
          if (!autoFillDisabled) {
            tmp[i].harvest_species = tmp[i].harvest_species?.map(x => {
              const spc = sizedOysters.find(y => y.id === x.specie_id);
              if (!spc) return { ...x };
              const percent = calcOysterPercent(spc, lineId);
              return {
                ...x,
                amount:
                  Math.round((percent ?? 0) * (tmp[i].amount_dz ?? 0)) / 100,
              };
            });
          }
        }
      } else if (key === 'amount_dz') {
        tmp[i] = {
          ...tmp[i],
          amount_dz: value,
          amount_pcs: value ? dozToPcs(value) : null,
        };
      } else if (key === 'amount_pcs') {
        tmp[i] = {
          ...tmp[i],
          amount_pcs: value,
          amount_dz: value ? pcsToDoz(value) : null,
        };
      } else if (key === 'return_amount_dz') {
        tmp[i] = {
          ...tmp[i],
          return_amount_pcs: value ? dozToPcs(value) : null,
          return_amount_dz: value,
        };
      } else if (key === 'return_amount_pcs') {
        tmp[i] = {
          ...tmp[i],
          return_amount_pcs: value,
          return_amount_dz: value ? pcsToDoz(value) : null,
        };
      } else if (key === 'harvest_groups') {
        const lengthSum =
          value?.reduce(
            (sum: number, x: IHarvestGroup) => sum + (x.line_length ?? 0),
            0,
          ) ?? 0;
        tmp[i] = { ...tmp[i], line_length: lengthSum > 0 ? lengthSum : null };
      }
    }
    setHarvestedLines(tmp);
  };
  const toggleMergedMode = (c: boolean) => {
    setIsMergedMode(c);
    if (c) {
      setHarvestedLines(
        harvestedLines.map(x => ({
          ...x,
          amount_dz: undefined,
          amount_pcs: undefined,
          mortality: undefined,
          harvest_species: undefined,
        })),
      );
    } else {
      setFormData(prv => ({
        ...prv,
        amount_dz: null,
        amount_pcs: null,
        mortality: null,
        harvest_species: undefined,
      }));
      setHarvestedLines(tmpLines =>
        tmpLines.map(x => {
          const dt = getNewLineData(
            allLines,
            x.line_id,
            sizedOysters,
            autoFillDisabled,
            c,
          );
          return {
            ...x,
            harvest_species: dt.harvest_species,
          };
        }),
      );
    }
  };
  const selectLines = (val: string[]) => {
    let tmp: ILineData[] = val.map(x =>
      getNewLineData(
        allLines,
        Number(x),
        sizedOysters,
        autoFillDisabled,
        isMergedMode,
      ),
    );
    for (let t of harvestedLines) {
      const i = tmp.findIndex(x => x.line_id === t.line_id);
      if (i > -1) {
        tmp[i] = { ...t };
      }
    }
    setHarvestedLines(tmp);
  };
  const updateForm = (key: keyof IFormData, value: any) => {
    if (
      [
        'amount_dz',
        'amount_pcs',
        'return_amount_dz',
        'return_amount_pcs',
        'mortality',
        'shell_length',
        'shell_length_max',
      ].includes(key)
    ) {
      value = value.length <= 0 ? null : Number(value);
    }
    let tmpForm = { ...formData, [key]: value };

    if (key === 'measurement_units') {
      const prv = formData.measurement_units ?? defaultForm.measurement_units;
      if (prv !== value) {
        if (isMergedMode) {
          tmpForm.mortality = formData.mortality
            ? value === 'pcs'
              ? dozToPcs(formData.mortality)
              : pcsToDoz(formData.mortality)
            : formData.mortality;
          tmpForm.harvest_species = formData.harvest_species?.map(x => ({
            ...x,
            amount: x.amount
              ? value === 'pcs'
                ? dozToPcs(x.amount)
                : pcsToDoz(x.amount)
              : x.amount,
          }));
          setHarvestedLines(tmpLines =>
            tmpLines.map(x => ({
              ...x,
              mortality: undefined,
              harvest_species: undefined,
              amount_pcs: undefined,
              return_amount_pcs: undefined,
            })),
          );
        } else {
          const tmpLines = harvestedLines.map(frm => ({
            ...frm,
            mortality: frm.mortality
              ? value === 'dz'
                ? pcsToDoz(frm.mortality)
                : dozToPcs(frm.mortality)
              : frm.mortality,
            harvest_species: frm.harvest_species?.map(x => ({
              ...x,
              amount: x.amount
                ? value === 'dz'
                  ? pcsToDoz(x.amount)
                  : dozToPcs(x.amount)
                : x.amount,
            })),
          }));
          setHarvestedLines(tmpLines);
          setFormData(prv => ({
            ...prv,
            mortality: null,
            harvest_species: undefined,
            amount_pcs: null,
            return_amount_pcs: null,
          }));
        }
      }
    } else if (key === 'amount_dz') {
      tmpForm.amount_pcs = value ? dozToPcs(value) : tmpForm.amount_pcs;
    } else if (key === 'amount_pcs') {
      tmpForm.amount_dz = value ? pcsToDoz(value) : tmpForm.amount_dz;
    } else if (key === 'return_amount_dz') {
      tmpForm.return_amount_pcs = value
        ? dozToPcs(value)
        : tmpForm.return_amount_pcs;
    } else if (key === 'return_amount_pcs') {
      tmpForm.return_amount_dz = value
        ? pcsToDoz(value)
        : tmpForm.return_amount_dz;
    }
    setFormData(tmpForm);
  };
  const validForm = () => {
    if (harvestedLines.length <= 0) {
      setErrorMsg(translate(lang, '_sel_one_line'));
      return null;
    }
    for (let line of harvestedLines) {
      const fd = allLines.find(x => x.id === line.line_id);
      if (!line.line_length) {
        setErrorMsg(
          translate(
            lang,
            '_input_num',
            `${fd?.farming_method} of ${fd?.farm_name} - ${fd?.line_name}`,
          ),
        );
        return null;
      }
      if (
        !isMergedMode &&
        (line.amount_pcs === null || line.amount_pcs === undefined)
      ) {
        setErrorMsg(
          translate(
            lang,
            '_input_amt',
            `${fd?.farming_method} of ${fd?.farm_name} - ${fd?.line_name}`,
          ),
        );
        return null;
      }
      if (line.inventories && line.inventories.length > 0) {
        if (!fd?.growing_cycle?.inventories_sum) {
          setErrorMsg(
            translate(
              lang,
              '_inventories',
              `${fd?.farm_name} - ${fd?.line_name}`,
            ),
          );
          return null;
        }
        for (let ivt of line.inventories) {
          const ivtData = fd.growing_cycle.inventories_sum.find(
            x => x.inventory_id === ivt.inventory_id,
          );
          if (!ivtData) {
            setErrorMsg(
              translate(
                lang,
                '_inv_found',
                ivt.inventory_id,
                `${fd?.farm_name} - ${fd?.line_name}`,
              ),
            );
            return null;
          }
          if (ivtData.quantity < (ivt.quantity ?? 0)) {
            setErrorMsg(
              translate(
                lang,
                '_inv_qty_over',
                `${ivtData.inventory_name}`,
                `${fd?.farm_name} - ${fd?.line_name}`,
              ),
            );
            return null;
          }
        }
      }
      if (line.harvest_groups && line.harvest_groups.length > 0) {
        let sum = 0;
        const lineSrc = allLines.find(x => x.id === line.line_id);
        for (const group of line.harvest_groups) {
          const seeding = lineSrc?.growing_cycle?.seedings.find(
            x => x.id === group.batch_seed_id,
          );
          const leftLen = seeding
            ? (seeding.basket_count ?? 0) - seeding.harvested
            : 0;
          sum += leftLen;
        }
        if (line.line_length > sum) {
          setErrorMsg(
            translate(
              lang,
              '%s count of line %s is over in %s %s',
              lineSrc?.farming_method,
              lineSrc?.line_name,
              sum,
              lineSrc?.farming_method,
            ),
          );
          return null;
        }
      }
    }
    if (!formData.shell_length) {
      setErrorMsg(translate(lang, '_input_shell_length'));
      return null;
    }
    if (
      formData.shell_length_max !== null &&
      (formData.shell_length === null ||
        formData.shell_length > formData.shell_length_max)
    ) {
      setErrorMsg(translate(lang, '_shell_length_must_less'));
      return null;
    }
    if (isMergedMode && !formData.amount_pcs) {
      setErrorMsg(translate(lang, '_input_amt_oysters_merged'));
      return null;
    }
    const totalLength = harvestedLines.reduce(
      (p, c) => p + (c.line_length ?? 0),
      0,
    );
    if (totalLength <= 0) {
      setErrorMsg(translate(lang, 'Total line length must be greater than 0'));
      return null;
    }
    const tt =
      formData.time_tracks && formData.time_tracks.length > 0
        ? toSecTimeTracks(formData.time_tracks)
        : undefined;
    const data = {
      ...formData,
      complete_date: toSecond(formData.complete_date),
      lines: harvestedLines.map(lineItem => {
        const divider = lineItem.harvest_groups?.length ?? 0;
        const amountPcs = isMergedMode
          ? ((formData.amount_pcs ?? 0) * (lineItem.line_length ?? 0)) /
            totalLength
          : lineItem.amount_pcs;
        return {
          ...lineItem,
          harvest_groups: lineItem.harvest_groups?.map((x, i) => ({
            ...x,
            amount: divideEqually(amountPcs ?? 0, divider, i),
            line_length:
              x.line_length ??
              divideEqually(lineItem.line_length ?? 0, divider, i),
          })),
          mortality: isMergedMode
            ? undefined
            : lineItem.mortality
              ? formData.measurement_units === 'pcs'
                ? lineItem.mortality
                : dozToPcs(lineItem.mortality)
              : undefined,
        };
      }),
      mortality:
        isMergedMode && formData.mortality
          ? formData.measurement_units === 'pcs'
            ? formData.mortality
            : dozToPcs(formData.mortality)
          : undefined,
      signature: signatureCanvas.current?.toDataURL() ?? null,
      time_tracks: tt ? tt : undefined,
      track_code: paramData?.track_code,
    };
    return data;
  };
  const confirmClick = async () => {
    const submitData = validForm();
    if (!submitData) {
      return;
    }
    if (overConfirm) {
      setDisabled(true);
      await overConfirm(submitData);
      setDisabled(false);
      return;
    }

    setDisabled(true);
    const res = confirmID
      ? await sendSingleRequest(
          { ...submitData, id: confirmID },
          'POST',
          'api/queue/confirm-bulk-harvest/oyster',
          true,
        )
      : await sendSingleRequest(
          submitData,
          'POST',
          'api/farm/line/bulk-oyster-harvest',
          true,
        );
    if (res.status) {
      dispatch(loadFarmsData());
      dispatch(loadInventories());
      dispatch(loadQueueData());
      dispatch(
        showFeedback({
          isMessage: true,
          type: 'success',
          message: translate(lang, res.data?.message ?? 'Success'),
        }),
      );
      onConfirm(res.data);
    } else {
      setErrorMsg(translate(lang, res.data?.message ?? 'Request failed'));
      setDisabled(false);
    }
  };

  useEffect(() => {
    if (sigCanLoaded && paramData && paramData.signature) {
      signatureCanvas.current?.fromDataURL(paramData.signature);
    }
  }, [paramData, sigCanLoaded]);

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      closable
      closeIcon={<CloseIcon />}
      width={1100}
    >
      <div className='section-modal wrap'>
        {!!errorMsg && (
          <Feedback
            message={errorMsg}
            type={'error'}
            theme='light'
            position={10}
            isGlobal
            onClose={() => setErrorMsg(undefined)}
          />
        )}
        <div className='d-flex align-center mb-17'>
          <Subtitle color='black-1' align='left' size={1} fontWeight={600}>
            {translate(lang, title ?? 'Oyster bulk harvest')}
          </Subtitle>
        </div>
        {!!warningMessage && (
          <div className='header-alert'>{warningMessage}</div>
        )}
        <div className='section'>
          <div className='mb-17'>
            <CheckboxButton
              label={translate(
                lang,
                'Enter oysters for every single line details',
              )}
              checked={!isMergedMode}
              onChange={e => toggleMergedMode(!e.target.checked)}
            />
          </div>
          {!isMergedMode && (
            <div className='d-flex mb-17 pl-17'>
              <div className='w-100'>
                {translate(lang, 'Measurement units')}
              </div>
              <div className='w-100'>
                <Radio.Group
                  className='d-flex align-center'
                  onChange={e =>
                    updateForm('measurement_units', e.target.value)
                  }
                  value={formData.measurement_units}
                >
                  <RadioButton label='dz' value='dz' />
                  <RadioButton className='ml-17' label='pcs' value='pcs' />
                </Radio.Group>
              </div>
            </div>
          )}
          <div className='mb-17'>
            <Dropdown
              label={translate(lang, '_sel_harvested_lines')}
              mode='multiple'
              options={linesOptions}
              value={harvestedLines.map(x => x.line_id.toString()) as any}
              onChange={(v: any) => selectLines(v)}
              showSearch={true}
              disabled={disabled}
            />
          </div>
        </div>
        {harvestedLines.map(line => (
          <LineSection
            key={line.line_id}
            line={line}
            isMergedMode={isMergedMode}
            allLines={allLines}
            lineOptions={linesOptions}
            weekBatches={weekBatches}
            sizedOysters={sizedOysters}
            measureUnit={formData.measurement_units ?? 'dz'}
            updateHarvestedLine={updateHarvestedLine}
            getSpatName={getSpatName}
            updateOysterSpecies={updateOysterSpecies}
            isTrackMortality={profile?.account?.track_mortality}
          />
        ))}
        <div className='section'>
          {isMergedMode && (
            <div className='d-flex mb-17 pl-17'>
              <div className='w-100'>
                {translate(lang, 'Measurement units')}
              </div>
              <div className='w-100'>
                <Radio.Group
                  className='d-flex align-center'
                  onChange={e =>
                    updateForm('measurement_units', e.target.value)
                  }
                  value={formData.measurement_units}
                >
                  <RadioButton label='dz' value='dz' />
                  <RadioButton className='ml-17' label='pcs' value='pcs' />
                </Radio.Group>
              </div>
            </div>
          )}
          {isMergedMode && (
            <>
              {!hideHarvestSpecies && (
                <div
                  className='item-row mb-17 flex-wrap px-7'
                  style={{ gap: 10 }}
                >
                  {sizedOysters.map(specie => {
                    const fm = formData.harvest_species?.find(
                      y => y.specie_id === specie.id,
                    );
                    return (
                      <div key={specie.id}>
                        <Input
                          type='number'
                          label={`${
                            specie.subtype ? specie.subtype + ' - ' : ''
                          }${specie.name}`}
                          value={numToStr(fm?.amount)}
                          onChange={e =>
                            updateOysterSpecies(
                              undefined,
                              specie.id,
                              e.target.value,
                            )
                          }
                          unit={formData.measurement_units}
                        />
                      </div>
                    );
                  })}
                </div>
              )}
              <div className='item-row mb-17 align-end'>
                <div className='item-cell'>
                  <Input
                    type='number'
                    label={translate(lang, 'Harvested amount')}
                    value={numToStr(
                      formData[
                        formData.measurement_units === 'pcs'
                          ? 'amount_pcs'
                          : 'amount_dz'
                      ],
                    )}
                    onChange={e =>
                      updateForm(
                        formData.measurement_units === 'pcs'
                          ? 'amount_pcs'
                          : 'amount_dz',
                        e.target.value,
                      )
                    }
                    unit={formData.measurement_units}
                    disabled={onlyView}
                  />
                </div>
                <div className='item-cell'>
                  <Input
                    type='number'
                    label={translate(lang, '_graded_oysters')}
                    value={numToStr(
                      formData[
                        formData.measurement_units === 'pcs'
                          ? 'return_amount_pcs'
                          : 'return_amount_dz'
                      ],
                    )}
                    onChange={e =>
                      updateForm(
                        formData.measurement_units === 'pcs'
                          ? 'return_amount_pcs'
                          : 'return_amount_dz',
                        e.target.value,
                      )
                    }
                    unit={formData.measurement_units}
                  />
                </div>
                <div className='item-cell'>
                  <Input
                    type='number'
                    label={translate(lang, 'Mortality')}
                    value={numToStr(formData.mortality)}
                    onChange={e => updateForm('mortality', e.target.value)}
                    unit={formData.measurement_units}
                  />
                </div>
              </div>
            </>
          )}
          <div className='item-row mb-17'>
            <div className='item-cell'>
              <Datepicker
                label={translate(lang, '_harvest_complete_date')}
                defaultValue={formData.complete_date}
                onChange={e =>
                  e && updateForm('complete_date', e.toDate().getTime())
                }
              />
            </div>
            <div className='item-cell'>
              <Input
                type='number'
                label={translate(lang, 'Shell length min')}
                value={numToStr(formData.shell_length)}
                onChange={e => updateForm('shell_length', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
            <div className='item-cell'>
              <Input
                type='number'
                label={translate(lang, 'Shell length max')}
                value={numToStr(formData.shell_length_max)}
                onChange={e => updateForm('shell_length_max', e.target.value)}
                unit={getUnitOfSpecie(unitType, 'size_mm')}
              />
            </div>
            <div className='item-cell'>
              <Input
                label={translate(lang, 'Harvest number')}
                type='number'
                value={formData.harvest_number ?? ''}
                onChange={e => updateForm('harvest_number', e.target.value)}
                disabled={onlyView}
              />
            </div>
          </div>
          <MoreFieldsForm
            type='OYSTER'
            form={formData}
            onChange={d => setFormData(d)}
          />
          <div className='mb-17'>
            <Input
              label={translate(lang, 'Comment')}
              placeholder={translate(lang, 'Comment')}
              type='textarea'
              onChange={e => updateForm('comment', e.target.value)}
              value={formData.comment ?? ''}
              name='comment'
            />
          </div>
          {!!formData.image_pdf_files && (
            <div className='mb-17'>
              <FilesWall
                label={translate(lang, 'Images & PDF Files')}
                fileList={formData.image_pdf_files}
                onChange={v => updateForm('image_pdf_files', v)}
                disabled={onlyView}
              />
            </div>
          )}
          <div className='mb-20'>
            <p className='mb-4 d-block paragrapgh paragrapgh--2 paragrapgh--400 paragrapgh--default'>
              {translate(lang, 'Signature')}
            </p>
            <div
              style={{
                border: '1px solid #EFEFEF',
                borderRadius: '4px',
                height: '200px',
              }}
            >
              <SignatureCanvas
                ref={r => {
                  signatureCanvas.current = r;
                  setSigCanLoaded(true);
                }}
                penColor='green'
                canvasProps={{ width: 550, height: 200 }}
              />
            </div>
          </div>
        </div>
        <div className='modal-button d-flex justify-end align-center'>
          <Button
            width='small'
            className='rsp-btn'
            size={2}
            type='fill'
            color='red'
            onClick={onCancel}
            disabled={disabled}
          >
            {translate(lang, 'Cancel')}
          </Button>
          <Button
            width='small'
            size={2}
            type='fill'
            color='green'
            className='rsp-btn ml-16'
            onClick={confirmClick}
            disabled={disabled}
          >
            {translate(lang, 'Confirm')}
          </Button>
        </div>
      </div>
      <ModalFeedbackView />
    </Modal>
  );
};

export default OysterBulkHarvestModal;
